import { css, html, nothing } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import "./paypal-button";
import { connect } from "pwa-helpers";
import store from "../../../store/store";
import { StoreState } from "../../../store/store.interface";
import paymentStyles from "../../../common/styles/payment-styles";
import { PaymentMethod, PaymentMethodTemplate } from "../payment-method";
import { booleanConverter } from "../../../common/converters";
import { ClientTokenDetails, PaymentDetails, PaymentType, RmsBookstoreAddress } from "../../checkout/shop-checkout.interface";
import { getAddressValidationError, getAddressValidationStatus, validateAddress } from "../../address/store/address-validation";
import { ActionStatus } from "../../../common/constants/action-status";
import { handleApiError } from "../../../common/utils/api-utils";
import { getActiveStepId, getClientTokenResponse, getPaymentDetails, getPaypalBillingAddress, getShippingAddress, getlastPaypalDetail, updateBillingAddress, resetPaymentDetails, updatePaymentDetails, updatePaypalBillingAddress, updatelastPaypalDetail } from "../../checkout/shop-checkout";
import { isValidPaypalDetail, renderCompletedAddress, renderPaypalDisplay } from "../../../common/utils/payment-util";
import { StreetValidationRequest } from "../../address/interfaces/address-validation.interface";
import emit from "../../../common/events";

/**
 * Paypal payment method that loads Paypal button. 
 * Renders Paypal payment results.
 */
 @customElement(`mhecomm-paypal-payment-method`)
 export class PaypalPaymentMethod extends connect(store)(PaymentMethod) implements PaymentMethodTemplate{

  static styles = [paymentStyles, css`
    #paypal-button {
      width: 285px;
    }
    #remove-paypal-button {
      width: fit-content;
    }
    #payment-method-details-container {
      flex-direction: column;
    }
  `, css`
    @media (min-width: 360px) {
      #payment-method-details-container {
        flex-direction: row;
      }
    }
  `];
  /**
   * Currency for Paypal transaction.
   */
  @property()
  currency = 'USD';

  /**
   * Flag to indicate the checkout contains shippable items.
   */
  @property({ converter: booleanConverter })
  shippable = false;

  @state()
  private paypalPaymentDetail: PaymentDetails | undefined;

  /**
   * Braintree Client token to initialize Paypal button.
   */
  @state()
  private clientToken = "";

  @state()
  private shippingAddress: RmsBookstoreAddress | undefined;

  private showPaylater = true;

  private paypalRedirectMessage = "You may be temporarily redirected to the PayPal site and confirm your payment and then returned to mheducation.com to complete your order.";

  stateChanged(state: StoreState): void {
    this.updateIsSelected(PaymentType.BRAINTREE_PAYPAL, state);

    if (getAddressValidationStatus(state) === ActionStatus.failed) {
      handleApiError(getAddressValidationError(state));
      return;
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mhecomm-paypal-checkout-info-collected', this.onCollectPaypalInfo);
    const clientTokenResponse: ClientTokenDetails | undefined = getClientTokenResponse(store.getState() as StoreState)
    this.clientToken = clientTokenResponse!.token;
    this.shippingAddress = getShippingAddress(store.getState() as StoreState)!;
    this.billingAddress = getPaypalBillingAddress(store.getState() as StoreState)!;
    if(this.billingAddress) {
      store.dispatch(updateBillingAddress(this.billingAddress));
    }
    const paymentDetail = getPaymentDetails(store.getState() as StoreState);
    if(paymentDetail) {
      if(isValidPaypalDetail(paymentDetail)) {
        this.paypalPaymentDetail = paymentDetail;
        store.dispatch(updatelastPaypalDetail(paymentDetail));
      } else {
        const paypalSavedDetail = getlastPaypalDetail(store.getState() as StoreState);
        if(paypalSavedDetail){
          this.paypalPaymentDetail = isValidPaypalDetail(paypalSavedDetail) ? paypalSavedDetail : undefined;
          store.dispatch(updatePaymentDetails(paypalSavedDetail));
        }
      }
    }
  }

  renderComp() {
    if (!this.isSelected) {
      return nothing;
    }

    if (this.paypalPaymentDetail) {
      return this.renderPaypalPaymentData(this.paypalPaymentDetail);
    } else {
      return this.renderPaypalButton();
    }
  }

  renderPaypalButton() {
    return html`
      <div class="d-flex justify-content-start">
        <div id="paypal-button">
          <mhecomm-paypal-button 
            amount = "${this.totalAmount}"
            currency = "${this.currency}"
            clientToken="${this.clientToken}"
            loaderMargins="mb-2"
            ?shippable=${this.shippable}
            ?userAutoLogin=${true}
            .shippingAddress = ${this.shippingAddress}>
          </mhecomm-paypal-button>
          ${this.showPaylater ? html`
          <mhecomm-paypal-button
            amount = "${this.totalAmount}"
            currency = "${this.currency}"
            clientToken="${this.clientToken}"
            clickEventMessage="PayPal Pay Later Start"
            ?shippable=${this.shippable}
            ?userAutoLogin=${true}
            ?paylater=${true}
            .shippingAddress = ${this.shippingAddress}>
          </mhecomm-paypal-button>
          `: nothing}
        </div>
      </div>
      ${this.paypalRedirectMessage ? html`<div class="pt-4">
        <p>${this.paypalRedirectMessage}</p>
      </div>`: nothing}`;
  }

  renderPaypalPaymentData(paymentDetails: PaymentDetails){
    return html`
      <div id="payment-method-details-container" class="d-flex pb-4">
        <div id="paypal-display-container" class="d-flex">
          ${renderPaypalDisplay(paymentDetails)}
        </div>
        <button
          id="remove-paypal-button"
          class="btn btn-link"
          data-automation-id="paypal-payment-details-remove"
          aria-label="Remove Paypal payment method"
          @click=${this._onClickRemove}>
          Remove
        </button>
      </div>
      ${this.billingAddress ? html`
        <div id="billing-address-details-container">
          <h3 class="fs-6"><b>Billing Address</b></h3>
          ${renderCompletedAddress(this.billingAddress)}
        </div>
      ` : nothing}
    `;
  }

  _onClickRemove() {
    this.paypalPaymentDetail = undefined;
    store.dispatch(resetPaymentDetails("Paypal"));
    store.dispatch(updatelastPaypalDetail(undefined));
  }

  public onCollectPaypalInfo(e: Event){
    const event = e as CustomEvent;
    const paypalBillingAddress: RmsBookstoreAddress = event.detail.billingAddress;
    const streetValidationRequest: StreetValidationRequest = {
      street1: paypalBillingAddress.address1,
      street2: paypalBillingAddress.address2,
      city: paypalBillingAddress.city,
      state: paypalBillingAddress.state,
      zipCode: paypalBillingAddress.zipCode,
      country: paypalBillingAddress.country
    }
    store.dispatch(validateAddress(streetValidationRequest)).unwrap().then(addressValidationInfo => {
      const validatedAddress: RmsBookstoreAddress = {
        firstName: paypalBillingAddress.firstName,
        lastName: paypalBillingAddress.lastName,
        ...addressValidationInfo,
        phoneNumber: paypalBillingAddress.phoneNumber,
      };
      store.dispatch(updatePaypalBillingAddress(validatedAddress));
      store.dispatch(updateBillingAddress(validatedAddress));
      this.updatePaypalPaymentDetails(event.detail.paymentDetails);
    }).catch(error => console.error("failed to validate address",error));
  }

  updatePaypalPaymentDetails(paymentDetails: PaymentDetails){
    store.dispatch(updatePaymentDetails(paymentDetails));
    //Trigger next step button click
    const activeStepId = getActiveStepId(store.getState() as StoreState);
    emit({
      element: this,
      name: `${activeStepId}-checkout-step-complete`,
      detail: {id: activeStepId},
    });
  }

  public async validatePaymentMethod(): Promise<void>{
    const paymentDetails = getPaymentDetails(store.getState() as StoreState)!;
    if(!paymentDetails.paymentNonce){
      throw new Error("Paypal payment failed.");
    }
  }

  paymentMethodEditAction(): void {
    return;
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-paypal-payment-method': PaypalPaymentMethod;
  }
}
